import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { SortSelect, sortByField } from '../components/SortSelect';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ILLER = [
  'İSTANBUL', 'ANKARA', 'İZMİR', 'BURSA', 'ANTALYA', 'ADANA', 'KOCAELİ', 'GAZİANTEP',
  'KONYA', 'MERSİN', 'DİYARBAKIR', 'KAYSERİ', 'ESKİŞEHİR', 'SAMSUN', 'DENİZLİ',
  'ŞANLIURFA', 'MALATYA', 'TRABZON', 'ERZURUM', 'VAN', 'BALIKESİR', 'MANİSA',
  'AYDIN', 'TEKİRDAĞ', 'EDİRNE', 'MUĞLA', 'HATAY', 'SAKARYA', 'DÜZCE', 'BOLU'
];

const FAALIYET_ALANLARI = [
  'İnşaat', 'Elektrik', 'Mekanik Tesisat', 'Tadilat & Bakım', 'Peyzaj',
  'Boya & Badana', 'Zemin Kaplama', 'Çatı & İzolasyon', 'Asansör',
  'Güvenlik Sistemleri', 'Yangın Sistemleri', 'Havalandırma & Klima',
  'Mobilya & Donatı', 'Temizlik Hizmetleri', 'Diğer'
];

const FirmaBilgileriPage = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [firmalar, setFirmalar] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [selectedFirma, setSelectedFirma] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortKey, setSortKey] = useState('firma_az');
  const [formData, setFormData] = useState({
    firma_adi: '',
    vergi_no: '',
    telefon: '',
    email: '',
    adres: '',
    yetkili_kisi: '',
    faaliyet_alani: '',
    il: '',
    ilce: '',
    iban: '',
    web_sitesi: '',
    notlar: ''
  });

  useEffect(() => {
    if (!user?.is_admin && !user?.has_bakim_onarim_access) {
      navigate('/dashboard');
      return;
    }
    fetchFirmalar();
  }, []);

  const fetchFirmalar = async () => {
    try {
      const response = await axios.get(`${API}/bakim-onarim/firma`, { withCredentials: true });
      setFirmalar(response.data);
    } catch (error) {
      console.error('Firma fetch error:', error);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      firma_adi: '', vergi_no: '', telefon: '', email: '', adres: '',
      yetkili_kisi: '', faaliyet_alani: '', il: '', ilce: '',
      iban: '', web_sitesi: '', notlar: ''
    });
    setEditMode(false);
    setSelectedFirma(null);
  };

  const handleSubmit = async () => {
    if (submitting) return;
    if (!formData.firma_adi.trim()) {
      alert('Firma adı zorunludur');
      return;
    }
    setSubmitting(true);
    try {
      if (editMode && selectedFirma) {
        await axios.put(`${API}/bakim-onarim/firma/${selectedFirma.id}`, formData, { withCredentials: true });
      } else {
        await axios.post(`${API}/bakim-onarim/firma`, formData, { withCredentials: true });
      }
      setDialogOpen(false);
      resetForm();
      fetchFirmalar();
    } catch (error) {
      console.error('Submit error:', error);
      alert('Hata: ' + (error.response?.data?.detail || error.message));
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = (firma) => {
    setSelectedFirma(firma);
    setFormData({
      firma_adi: firma.firma_adi || '',
      vergi_no: firma.vergi_no || '',
      telefon: firma.telefon || '',
      email: firma.email || '',
      adres: firma.adres || '',
      yetkili_kisi: firma.yetkili_kisi || '',
      faaliyet_alani: firma.faaliyet_alani || '',
      il: firma.il || '',
      ilce: firma.ilce || '',
      iban: firma.iban || '',
      web_sitesi: firma.web_sitesi || '',
      notlar: firma.notlar || ''
    });
    setEditMode(true);
    setDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Bu firmayı silmek istediğinizden emin misiniz?')) return;
    try {
      await axios.delete(`${API}/bakim-onarim/firma/${id}`, { withCredentials: true });
      fetchFirmalar();
    } catch (error) {
      console.error('Delete error:', error);
      alert('Silme hatası: ' + (error.response?.data?.detail || error.message));
    }
  };

  const filteredFirmalar = firmalar.filter(f => {
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    return (
      (f.firma_adi || '').toLowerCase().includes(term) ||
      (f.yetkili_kisi || '').toLowerCase().includes(term) ||
      (f.faaliyet_alani || '').toLowerCase().includes(term) ||
      (f.il || '').toLowerCase().includes(term) ||
      (f.vergi_no || '').includes(term)
    );
  });
  const SORT_DEFS_FIRMA = {
    firma_az: { field: 'firma_adi', dir: 1 }, firma_za: { field: 'firma_adi', dir: -1 },
    il_az: { field: 'il', dir: 1 }, newest: { field: 'created_at', dir: -1 }, oldest: { field: 'created_at', dir: 1 }
  };
  const sortedFirmalar = sortByField(filteredFirmalar, sortKey, SORT_DEFS_FIRMA);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-950 flex items-center justify-center">
        <div className="text-white">Yükleniyor...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-950 via-gray-900 to-gray-950">
      {/* Header */}
      <header className="bg-gray-900/50 border-b border-gray-800 backdrop-blur-sm sticky top-0 z-10">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <img src="/logo.png" alt="Logo" className="w-12 h-12 object-contain cursor-pointer" onClick={() => navigate('/dashboard')} />
              <div>
                <h1 className="text-xl font-bold text-white">Kurumsal Is Yonetimi, Takip ve Koordinasyon Platformu</h1>
                <p className="text-xs text-gray-400">Bakim-Onarim / Firma Bilgileri</p>
              </div>
            </div>
            <div className="flex items-center space-x-1">
              <button onClick={() => navigate('/')} className="px-4 py-2 text-gray-300 hover:text-white hover:bg-gray-800/50 rounded-lg transition-colors">Anasayfa</button>
              <span className="text-gray-600">|</span>
              <button onClick={() => navigate('/dashboard')} className="px-4 py-2 text-gray-300 hover:text-white hover:bg-gray-800/50 rounded-lg transition-colors">Dashboard</button>
              <span className="text-gray-600">|</span>
              <button onClick={() => navigate('/bakim-onarim/dogrudan-temin')} className="px-4 py-2 text-gray-300 hover:text-white hover:bg-gray-800/50 rounded-lg transition-colors">Dogrudan Temin</button>
              <span className="text-gray-600">|</span>
              <button onClick={() => navigate('/bakim-onarim/firma-bilgileri')} className="px-4 py-2 text-gray-300 hover:text-white hover:bg-gray-800/50 rounded-lg transition-colors border-b-2 border-amber-500">Firma Bilgileri</button>
              <div className="ml-4 border-l border-gray-700 pl-4 flex items-center space-x-4">
                <div className="flex items-center space-x-3 bg-gray-800/50 rounded-lg px-4 py-2 cursor-pointer hover:bg-gray-800/70 transition-colors" onClick={() => navigate('/profile')}>
                  <img src={user?.picture} alt={user?.name} className="w-8 h-8 rounded-full" />
                  <div className="text-left">
                    <p className="text-sm font-medium text-white">{user?.name}</p>
                    <p className="text-xs text-gray-400">{user?.is_admin ? 'Admin' : 'Kullanıcı'}</p>
                  </div>
                </div>
                <Button onClick={handleLogout} variant="ghost" className="text-red-400 hover:text-red-300">Cikis</Button>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="container mx-auto px-4 py-8">
        <div className="mb-8 flex items-center justify-between">
          <div>
            <h2 className="text-3xl font-bold text-white mb-2" data-testid="firma-page-title">Firma Bilgileri</h2>
            <p className="text-gray-400">Bakim-Onarim modülünde kullanilan firmalari yönetin</p>
          </div>
          <div className="flex gap-3">
            {!user?.is_viewer && (
              <Button
                onClick={() => { resetForm(); setDialogOpen(true); }}
                className="bg-amber-600 hover:bg-amber-700 text-white"
                data-testid="new-firma-btn"
              >
                <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                Yeni Firma Ekle
              </Button>
            )}
          </div>
        </div>

        {/* Search */}
        <div className="mb-6 flex items-center gap-3">
          <Input
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Firma adı, yetkili kisi, faaliyet alanı veya vergi no ile ara..."
            className="bg-gray-800 border-gray-700 text-white max-w-lg"
            data-testid="firma-search-input"
          />
          <SortSelect value={sortKey} onChange={setSortKey} customOptions={[
            { value: 'firma_az', label: 'Firma Adı (A-Z)' }, { value: 'firma_za', label: 'Firma Adı (Z-A)' },
            { value: 'il_az', label: 'İl (A-Z)' },
            { value: 'newest', label: 'En Yeni' }, { value: 'oldest', label: 'En Eski' }
          ]} />
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <Card className="bg-gray-900 border-gray-800">
            <CardContent className="pt-6">
              <p className="text-xs text-gray-400 mb-1">Toplam Firma</p>
              <p className="text-3xl font-bold text-white">{firmalar.length}</p>
            </CardContent>
          </Card>
          <Card className="bg-gray-900 border-gray-800">
            <CardContent className="pt-6">
              <p className="text-xs text-gray-400 mb-1">Faaliyet Alanı</p>
              <p className="text-3xl font-bold text-amber-400">{new Set(firmalar.map(f => f.faaliyet_alani).filter(Boolean)).size}</p>
            </CardContent>
          </Card>
          <Card className="bg-gray-900 border-gray-800">
            <CardContent className="pt-6">
              <p className="text-xs text-gray-400 mb-1">Filtrelenen</p>
              <p className="text-3xl font-bold text-blue-400">{filteredFirmalar.length}</p>
            </CardContent>
          </Card>
        </div>

        {/* Firma Table */}
        <Card className="bg-gray-900 border-gray-800">
          <CardHeader>
            <CardTitle className="text-white">Kayitli Firmalar ({sortedFirmalar.length})</CardTitle>
          </CardHeader>
          <CardContent>
            {sortedFirmalar.length === 0 ? (
              <div className="text-center py-12">
                <p className="text-gray-400">
                  {firmalar.length === 0 ? 'Henüz firma eklenmemis' : 'Aramaniza uygun firma bulunamadi'}
                </p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm text-left text-gray-300" data-testid="firma-table">
                  <thead className="text-xs text-gray-400 uppercase bg-gray-800">
                    <tr>
                      <th className="px-4 py-3">S.NO</th>
                      <th className="px-4 py-3">Firma Adi</th>
                      <th className="px-4 py-3">Vergi No</th>
                      <th className="px-4 py-3">Yetkili Kisi</th>
                      <th className="px-4 py-3">Telefon</th>
                      <th className="px-4 py-3">Faaliyet Alani</th>
                      <th className="px-4 py-3">Il</th>
                      {user?.is_admin && <th className="px-4 py-3">Son Düzenleyen</th>}
                      <th className="px-4 py-3">Islemler</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sortedFirmalar.map((firma, index) => (
                      <tr key={firma.id} className="border-b border-gray-800 hover:bg-gray-800/50" data-testid={`firma-row-${firma.id}`}>
                        <td className="px-4 py-3">{index + 1}</td>
                        <td className="px-4 py-3 font-medium text-white">{firma.firma_adi}</td>
                        <td className="px-4 py-3">{firma.vergi_no || '-'}</td>
                        <td className="px-4 py-3">{firma.yetkili_kisi || '-'}</td>
                        <td className="px-4 py-3">{firma.telefon || '-'}</td>
                        <td className="px-4 py-3">
                          {firma.faaliyet_alani && (
                            <span className="px-2 py-0.5 bg-amber-900/30 text-amber-400 text-xs rounded">{firma.faaliyet_alani}</span>
                          )}
                          {!firma.faaliyet_alani && '-'}
                        </td>
                        <td className="px-4 py-3">{firma.il || '-'}</td>
                        {user?.is_admin && (
                          <td className="px-4 py-3 text-xs text-gray-400">
                            {firma.last_modified_by_name || '-'}
                            {firma.last_modified_at && <div className="text-gray-500 mt-0.5">{new Date(firma.last_modified_at).toLocaleString('tr-TR')}</div>}
                          </td>
                        )}
                        <td className="px-4 py-3">
                          <div className="flex gap-2">
                            {!user?.is_viewer && (
                              <>
                                <Button onClick={() => handleEdit(firma)} size="sm" variant="ghost" className="text-blue-400 hover:text-blue-300" data-testid={`edit-firma-${firma.id}`}>Düzenle</Button>
                                {user?.is_admin && (
                                  <Button onClick={() => handleDelete(firma.id)} size="sm" variant="ghost" className="text-red-400 hover:text-red-300" data-testid={`delete-firma-${firma.id}`}>Sil</Button>
                                )}
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </main>

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={(open) => { if (!open) { setDialogOpen(false); resetForm(); } else { setDialogOpen(true); } }}>
        <DialogContent className="bg-gray-900 border-gray-800 text-white max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editMode ? 'Firma Düzenle' : 'Yeni Firma Ekle'}</DialogTitle>
            <DialogDescription className="text-gray-400">Firma bilgilerini girin</DialogDescription>
          </DialogHeader>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 py-4">
            <div className="space-y-2 md:col-span-2">
              <Label>FİRMA ADI *</Label>
              <Input value={formData.firma_adi} onChange={(e) => setFormData({ ...formData, firma_adi: e.target.value })} className="bg-gray-800 border-gray-700 text-white" placeholder="Firma adini girin" data-testid="firma-adi-input" />
            </div>
            <div className="space-y-2">
              <Label>VERGİ NO</Label>
              <Input value={formData.vergi_no} onChange={(e) => setFormData({ ...formData, vergi_no: e.target.value })} className="bg-gray-800 border-gray-700 text-white" placeholder="1234567890" />
            </div>
            <div className="space-y-2">
              <Label>YETKİLİ KİŞİ</Label>
              <Input value={formData.yetkili_kisi} onChange={(e) => setFormData({ ...formData, yetkili_kisi: e.target.value })} className="bg-gray-800 border-gray-700 text-white" placeholder="Ad Soyad" />
            </div>
            <div className="space-y-2">
              <Label>TELEFON</Label>
              <Input value={formData.telefon} onChange={(e) => setFormData({ ...formData, telefon: e.target.value })} className="bg-gray-800 border-gray-700 text-white" placeholder="0212 123 45 67" />
            </div>
            <div className="space-y-2">
              <Label>E-POSTA</Label>
              <Input value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} className="bg-gray-800 border-gray-700 text-white" placeholder="info@firma.com" />
            </div>
            <div className="space-y-2">
              <Label>FAALİYET ALANI</Label>
              <Select value={formData.faaliyet_alani} onValueChange={(value) => setFormData({ ...formData, faaliyet_alani: value })}>
                <SelectTrigger className="bg-gray-800 border-gray-700 text-white"><SelectValue placeholder="Seciniz" /></SelectTrigger>
                <SelectContent className="bg-gray-800 border-gray-700 text-white max-h-60">
                  {FAALIYET_ALANLARI.map((alan) => (<SelectItem key={alan} value={alan}>{alan}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>İL</Label>
              <Select value={formData.il} onValueChange={(value) => setFormData({ ...formData, il: value })}>
                <SelectTrigger className="bg-gray-800 border-gray-700 text-white"><SelectValue placeholder="Il secin" /></SelectTrigger>
                <SelectContent className="bg-gray-800 border-gray-700 text-white max-h-60">
                  {ILLER.map((il) => (<SelectItem key={il} value={il}>{il}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>İLÇE</Label>
              <Input value={formData.ilce} onChange={(e) => setFormData({ ...formData, ilce: e.target.value })} className="bg-gray-800 border-gray-700 text-white" placeholder="Ilce" />
            </div>
            <div className="space-y-2">
              <Label>WEB SİTESİ</Label>
              <Input value={formData.web_sitesi} onChange={(e) => setFormData({ ...formData, web_sitesi: e.target.value })} className="bg-gray-800 border-gray-700 text-white" placeholder="www.firma.com" />
            </div>
            <div className="space-y-2 md:col-span-2">
              <Label>ADRES</Label>
              <Textarea value={formData.adres} onChange={(e) => setFormData({ ...formData, adres: e.target.value })} className="bg-gray-800 border-gray-700 text-white" rows={2} placeholder="Firma adresi" />
            </div>
            <div className="space-y-2 md:col-span-2">
              <Label>IBAN</Label>
              <Input value={formData.iban} onChange={(e) => setFormData({ ...formData, iban: e.target.value })} className="bg-gray-800 border-gray-700 text-white" placeholder="TR00 0000 0000 0000 0000 0000 00" />
            </div>
            <div className="space-y-2 md:col-span-2">
              <Label>NOTLAR</Label>
              <Textarea value={formData.notlar} onChange={(e) => setFormData({ ...formData, notlar: e.target.value })} className="bg-gray-800 border-gray-700 text-white" rows={2} placeholder="Ek notlar..." />
            </div>
          </div>

          <DialogFooter>
            <Button onClick={() => { setDialogOpen(false); resetForm(); }} variant="ghost" className="text-gray-400">Iptal</Button>
            <Button onClick={handleSubmit} disabled={submitting} className="bg-amber-600 hover:bg-amber-700 text-white" data-testid="firma-submit-btn">
              {submitting ? 'Kaydediliyor...' : (editMode ? 'Güncelle' : 'Ekle')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default FirmaBilgileriPage;
